import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { LogoFull } from '../components/Logo';

export default function Signup() {
  const location = useLocation();
  const [referralCode, setReferralCode] = useState('');
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '',
    address1: '', address2: '', city: '', county: '', postcode: '',
  });
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pick up ?ref= from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref.toUpperCase());
  }, [location.search]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    if (!acceptedTerms) return setError('You must accept the Terms & Conditions to continue');
    setLoading(true);
    try {
      await api.auth.signup({ ...form, referralCode });
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo"><LogoFull iconSize={64} /></div>
          <div style={{ fontSize: 48, margin: '16px 0' }}>&#9993;</div>
          <h1 className="auth-title">Check your email</h1>
          <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
            We've sent a verification link to <strong style={{ color: 'var(--text)' }}>{form.email}</strong>.
            Please click the link to activate your account.
          </p>
          {referralCode && (
            <p style={{ color: 'var(--success)', fontSize: 13, marginBottom: 16 }}>
              &#10003; Referral code applied — you'll receive 50 credits when you verify your email!
            </p>
          )}
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>
            Didn't receive it?{' '}
            <Link to={`/resend-verification?email=${encodeURIComponent(form.email)}`}
              style={{ color: 'var(--accent)' }}>
              Resend verification email
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 520 }}>
        <div className="auth-logo"><LogoFull iconSize={56} /></div>
        <h1 className="auth-title">Create account</h1>
        <p className="auth-subtitle">
          {referralCode
            ? `You've been referred! Sign up for 50 free credits.`
            : `Get started — we'll send you a free sticker for your car`}
        </p>

        {/* Referral banner */}
        {referralCode && (
          <div style={{
            background: 'rgba(82,255,138,0.08)', border: '1px solid rgba(82,255,138,0.3)',
            borderRadius: 10, padding: '10px 16px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>&#127881;</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--success)' }}>
                Referral code applied: {referralCode}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                You'll receive 50 credits instead of 20 when you verify your email.
              </div>
            </div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First name</label>
              <input type="text" name="firstName" value={form.firstName}
                onChange={handleChange} placeholder="Jane" required autoComplete="given-name" />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input type="text" name="lastName" value={form.lastName}
                onChange={handleChange} placeholder="Smith" required autoComplete="family-name" />
            </div>
          </div>

          <div className="form-group">
            <label>Email address</label>
            <input type="email" name="email" value={form.email}
              onChange={handleChange} placeholder="you@example.com" required autoComplete="email" />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input type="password" name="password" value={form.password}
              onChange={handleChange} placeholder="Min. 8 characters" required autoComplete="new-password" />
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '20px 0', paddingTop: 20 }}>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Postal address — so we can send your free sticker
            </p>
            <div className="form-group">
              <label>Address line 1</label>
              <input type="text" name="address1" value={form.address1}
                onChange={handleChange} placeholder="123 High Street" required autoComplete="address-line1" />
            </div>
            <div className="form-group">
              <label>Address line 2 (optional)</label>
              <input type="text" name="address2" value={form.address2}
                onChange={handleChange} placeholder="Flat 4" autoComplete="address-line2" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City / Town</label>
                <input type="text" name="city" value={form.city}
                  onChange={handleChange} placeholder="Manchester" required autoComplete="address-level2" />
              </div>
              <div className="form-group">
                <label>County (optional)</label>
                <input type="text" name="county" value={form.county}
                  onChange={handleChange} placeholder="Greater Manchester" autoComplete="address-level1" />
              </div>
            </div>
            <div className="form-group">
              <label>Postcode</label>
              <input type="text" name="postcode" value={form.postcode}
                onChange={handleChange} placeholder="M1 1AA" required autoComplete="postal-code"
                style={{ textTransform: 'uppercase', maxWidth: 160 }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <input type="checkbox" id="terms" checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }} />
            <label htmlFor="terms" style={{ fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
              I agree to the <a href="/terms" target="_blank" style={{ color: 'var(--accent)' }}>Terms &amp; Conditions</a> and <a href="/privacy" target="_blank" style={{ color: 'var(--accent)' }}>Privacy Policy</a>
            </label>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><span className="spinner"></span> Creating account...</> : 'Create account'}
          </button>
        </form>

        <p className="auth-link-text">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
